import { useEffect, useRef, useState } from 'react';
import { LogOut, UserCircle } from 'lucide-react';
import type { User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { useTheme, type ThemePreference } from '../contexts/ThemeContext';

interface AccountMenuProps {
  user: User;
  onLogout: () => void;
  /** Smaller email on narrow bars (e.g. document viewer). */
  compactEmail?: boolean;
}

function displayNameFromUser(user: User): string {
  const meta = user.user_metadata as Record<string, unknown> | undefined;
  return (
    (typeof meta?.full_name === 'string' && meta.full_name) ||
    (typeof meta?.name === 'string' && meta.name) ||
    ''
  );
}

const headerEnvironmentLabel =
  import.meta.env.VITE_ENVIRONMENT?.trim() || 'local';

export function AccountMenu({ user, onLogout, compactEmail }: AccountMenuProps) {
  const { preference, setPreference } = useTheme();
  const [open, setOpen] = useState(false);
  const [productEmails, setProductEmails] = useState(true);
  const [weeklyDigest, setWeeklyDigest] = useState(false);
  const [nameDraft, setNameDraft] = useState(() => displayNameFromUser(user));
  const [nameSaving, setNameSaving] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  const displayName = displayNameFromUser(user);

  useEffect(() => {
    setNameDraft(displayName);
  }, [user.id, displayName]);

  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (wrapRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const saveDisplayName = async () => {
    const trimmed = nameDraft.trim();
    setNameError(null);
    setNameSaving(true);
    const { error } = await supabase.auth.updateUser({
      data: { full_name: trimmed, name: trimmed },
    });
    setNameSaving(false);
    if (error) {
      setNameError(error.message);
      return;
    }
  };

  return (
    <div ref={wrapRef} className="flex shrink-0 items-center gap-3">
      <div
        className={`hidden shrink-0 flex-col items-end gap-0.5 text-right ${compactEmail ? 'sm:flex' : 'max-w-[200px] md:flex md:max-w-none'}`}
      >
        <span
          className="truncate text-sm text-slate-600 dark:text-slate-300"
          title={user.email ?? undefined}
        >
          {user.email}
        </span>
        <span
          className="max-w-full truncate text-[11px] leading-tight text-slate-500 dark:text-slate-400"
          title={`${headerEnvironmentLabel} · ${__APP_VERSION__}`}
        >
          {headerEnvironmentLabel} · {__APP_VERSION__}
        </span>
      </div>
      <div className="relative">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className={`flex items-center rounded-lg p-2 text-slate-700 transition-colors hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800 ${open ? 'bg-slate-100 dark:bg-slate-800' : ''}`}
          title="My account"
          aria-expanded={open}
          aria-haspopup="true"
        >
          <UserCircle className="h-6 w-6" aria-hidden />
          <span className="sr-only">My account</span>
        </button>
        {open && (
          <div
            className="absolute right-0 top-full z-[100] mt-2 w-[min(calc(100vw-2rem),22rem)] rounded-xl border border-slate-200 bg-white py-3 shadow-lg dark:border-slate-700 dark:bg-slate-900"
            role="dialog"
            aria-label="Account settings"
          >
            <div className="border-b border-slate-100 px-4 pb-3 dark:border-slate-800">
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                My account
              </p>
              <p className="mt-1 truncate text-sm font-semibold text-slate-900 dark:text-slate-100">
                {displayName || 'Signed in'}
              </p>
              <p className="truncate text-xs text-slate-600 dark:text-slate-400">{user.email}</p>
            </div>

            <div className="max-h-[min(70vh,24rem)] overflow-y-auto px-4 py-3">
              <section className="space-y-3">
                <h3 className="text-xs font-semibold text-slate-800 dark:text-slate-200">Profile</h3>
                <div>
                  <label className="mb-1 block text-xs text-slate-500 dark:text-slate-400">
                    Display name
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={nameDraft}
                      onChange={(e) => setNameDraft(e.target.value)}
                      placeholder="Your name"
                      className="min-w-0 flex-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-slate-400 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:placeholder:text-slate-500"
                    />
                    <button
                      type="button"
                      disabled={nameSaving || nameDraft.trim() === displayName}
                      onClick={() => void saveDisplayName()}
                      className="shrink-0 rounded-lg bg-slate-800 px-3 py-2 text-sm font-medium text-white hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-slate-200 dark:text-slate-900 dark:hover:bg-white"
                    >
                      {nameSaving ? '…' : 'Save'}
                    </button>
                  </div>
                  {nameError && (
                    <p className="mt-1 text-xs text-red-600 dark:text-red-400" role="alert">
                      {nameError}
                    </p>
                  )}
                </div>
                <div>
                  <label className="mb-1 block text-xs text-slate-500 dark:text-slate-400">Email</label>
                  <input
                    type="email"
                    readOnly
                    value={user.email ?? ''}
                    className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800 dark:border-slate-700 dark:bg-slate-800/80 dark:text-slate-200"
                  />
                </div>
              </section>

              <section className="mt-5 space-y-3 border-t border-slate-100 pt-4 dark:border-slate-800">
                <h3 className="text-xs font-semibold text-slate-800 dark:text-slate-200">
                  Notifications
                </h3>
                <label className="flex cursor-pointer items-center justify-between gap-3 text-sm text-slate-700 dark:text-slate-300">
                  <span>Product updates</span>
                  <input
                    type="checkbox"
                    checked={productEmails}
                    onChange={(e) => setProductEmails(e.target.checked)}
                    className="h-4 w-4 rounded border-slate-300 text-slate-800 dark:border-slate-600 dark:bg-slate-800"
                  />
                </label>
                <label className="flex cursor-pointer items-center justify-between gap-3 text-sm text-slate-700 dark:text-slate-300">
                  <span>Weekly digest</span>
                  <input
                    type="checkbox"
                    checked={weeklyDigest}
                    onChange={(e) => setWeeklyDigest(e.target.checked)}
                    className="h-4 w-4 rounded border-slate-300 text-slate-800 dark:border-slate-600 dark:bg-slate-800"
                  />
                </label>
              </section>

              <section className="mt-5 space-y-2 border-t border-slate-100 pt-4 dark:border-slate-800">
                <h3 className="text-xs font-semibold text-slate-800 dark:text-slate-200">Appearance</h3>
                <label className="block text-xs text-slate-500 dark:text-slate-400">Theme</label>
                <select
                  value={preference}
                  onChange={(e) => setPreference(e.target.value as ThemePreference)}
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
                >
                  <option value="light">Light</option>
                  <option value="dark">Dark</option>
                  <option value="system">System</option>
                </select>
              </section>

              <section className="mt-5 rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-xs text-slate-600 dark:border-slate-700 dark:bg-slate-800/50 dark:text-slate-400">
                <p className="font-medium text-slate-700 dark:text-slate-300">Security</p>
                <p className="mt-1 leading-snug">
                  Password and sign-in methods are managed by your identity provider. Contact your admin
                  to reset access.
                </p>
              </section>

              <p className="mt-4 text-[11px] leading-snug text-slate-400 dark:text-slate-500">
                Notification checkboxes are local only. Display name and theme are saved (theme on this
                device).
              </p>
            </div>
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={onLogout}
        className="flex items-center rounded-lg bg-slate-200 p-2 text-slate-700 transition-colors hover:bg-slate-300 dark:bg-slate-700 dark:text-slate-200 dark:hover:bg-slate-600"
        title="Logout"
      >
        <LogOut className="h-4 w-4" />
      </button>
    </div>
  );
}
